import {
  Box,
  Container,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerOverlay,
} from '@chakra-ui/react';
import { FC, memo, useEffect } from 'react';

import { layoutStyled } from '@components/Layout/Layout.styles';
import { ILayout } from '@components/Layout/Layout.types';
import LockScreen from '@components/LockScreen';
import Navbar from '@components/Navbar';
import NavbarOnly from '@components/NavbarOnly';
import RenderIf from '@components/RenderIf';
import SEO from '@components/SeoComponent/Seo';
import Sidebar from '@components/Sidebar';
import ReveivingScan from '@containers/Inbound/Receiving/Scan';
import { configEnv } from '@libraries/config';
import { useRouter } from 'next/router';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import {
  appTheme,
  isLockedState,
  receivingIsOpen,
  sidebarIsOpen,
} from 'store/Common';

const Layout: FC<ILayout> = ({
  children,
  title,
  description,
  imageUrl,
  urlCanonical,
  menuSelected,
  isNavbar = true,
  isSidebar = true,
  isNoShadow,
  isFullWidth = false,
  ...props
}): JSX.Element => {
  const layoutType = useRecoilValue(appTheme);
  const locked = useRecoilValue(isLockedState);
  const setLocked = useSetRecoilState(isLockedState);
  const isOpenSidebar = useRecoilValue(sidebarIsOpen);
  const setSidebarOpen = useSetRecoilState(sidebarIsOpen);
  const router = useRouter();

  const isOpenReceiving = useRecoilValue(receivingIsOpen);
  const setReceivingOpen = useSetRecoilState(receivingIsOpen);
  useEffect(() => {
    setSidebarOpen(false);
  }, [router]);

  return (
    <>
      <SEO
        title={title}
        description={description}
        imageUrl={imageUrl}
        urlCanonical={urlCanonical}
        type="website"
      />
      {locked && <LockScreen onUnlock={() => setLocked(false)} />}
      {process.env.NODE_ENV === 'development' && (
        <Box
          position="fixed"
          bottom="0"
          left="0"
          width="100vw"
          height="20px"
          bg="#dd6b20"
          color="white"
          zIndex={2000}
          display="flex"
          alignItems="center"
          justifyContent="center"
          fontWeight="bold"
          fontSize="sm"
          pointerEvents="none"
        >
          DEVELOPMENT
        </Box>
      )}
      <Box minH="100vh" display="flex" w="100vw" {...props}>
        <Box w="280px" {...(layoutType !== 'sidebar' && { display: 'none' })}>
          <RenderIf isTrue={isSidebar && layoutType === 'sidebar'}>
            <Sidebar menuSelected={menuSelected} />
          </RenderIf>
        </Box>
        <Drawer
          isOpen={isOpenSidebar}
          placement="left"
          onClose={() => setSidebarOpen(false)}
        >
          <DrawerOverlay />
          <DrawerContent p="0" maxW="280px">
            <DrawerBody p="0" overflow="hidden">
              <Sidebar isMobile menuSelected={menuSelected} />
            </DrawerBody>
          </DrawerContent>
        </Drawer>
        <Box
          bgImage={{ base: 'none', lg: `${configEnv.assetUrl}/img/wave.svg` }}
          bgRepeat="no-repeat"
          bgPos="center"
          bgSize="cover"
          {...layoutStyled()}
        >
          <RenderIf isTrue={isNavbar && layoutType === 'sidebar'}>
            <Navbar isNoShadow={isNoShadow} />
          </RenderIf>
          <RenderIf isTrue={isNavbar && layoutType === 'navbar'}>
            <NavbarOnly isNoShadow={isNoShadow} menuSelected={menuSelected} />
          </RenderIf>
          <Container
            as="main"
            id="container"
            maxW="1920px"
            px={{
              base: '4',
              lg: '8',
            }}
            pt="4"
            pb="8"
            {...(isFullWidth && { maxW: 'unset', p: '0 !important' })}
          >
            {children}
          </Container>
        </Box>
      </Box>
      {isOpenReceiving && (
        <ReveivingScan
          isOpen={isOpenReceiving}
          onClose={() => setReceivingOpen(false)}
        />
      )}
    </>
  );
};

export default memo(Layout);
